/**
 * exerciseImages.js — central image picker for the exercise library.
 *
 * Every exercise's `image:` field calls getExerciseImage(muscle, id). The
 * helper looks up the muscle in the survivors map, then picks ONE image:
 *
 *   - 1 image for that muscle  → returns it
 *   - 2+ images for that muscle → deterministic pick by exercise id
 *     (same exercise always shows the same image; refresh doesn't flicker)
 *
 * Muscles without dedicated images fall back to 'full-body'.
 *
 * Source of truth for which images exist:
 *   /app/frontend/public/generated/strength-*.png  (NanoBanana set, survivors only)
 *   /app/frontend/public/library/categories/*.jpg  (founder uploads)
 *
 * To add new images later:
 *   1. Drop the file in the right folder
 *   2. Append the path to MUSCLE_IMAGES[muscle] below
 *   3. Done — no per-exercise edits needed
 */

// Survivors map — every entry verified to exist on disk.
// Mix of NanoBanana renders (we paid for them) + founder uploads (your real photography).
// Randomized PER EXERCISE within the muscle group.
const MUSCLE_IMAGES = {
  arms: [
    '/generated/strength-arms.png',
    '/generated/strength-arms-v2.png',
    '/generated/strength-arms-v3.png',
    '/library/categories/arms.jpg',
  ],
  back: [
    '/generated/strength-back.png',
    '/generated/strength-back-v2.png',
    '/generated/strength-back-v3.png',
    '/library/categories/back.jpg',
  ],
  calves: [
    '/generated/strength-calves-v1.png',
    '/library/categories/calves.jpg',
  ],
  chest: [
    '/generated/strength-chest.png',
    '/generated/strength-chest-v2.png',
    '/generated/strength-chest-v3.png',
    '/library/categories/chest.jpg',
  ],
  core: [
    '/generated/strength-core.png',
    '/library/categories/core.jpg',
  ],
  forearms: [
    '/generated/strength-forearms-v1.png',
    '/generated/strength-forearms-v2.png',
    '/library/categories/forearms.jpg',
  ],
  glutes: [
    '/library/categories/glutes.jpg',
  ],
  hamstrings: [
    '/library/categories/hamstrings.jpg',
  ],
  legs: [
    '/generated/strength-legs.png',
    '/generated/strength-lower.png',
    '/generated/strength-quads.png',
    '/library/categories/legs.jpg',
  ],
  neck: [
    '/library/categories/neck.jpg',
  ],
  shoulders: [
    '/generated/strength-shoulders.png',
    '/library/categories/shoulders.jpg',
  ],
  'full-body': [
    '/generated/strength-fullbody.png',
    '/generated/strength-posterior.png',
    '/generated/strength-upper.png',
    '/library/categories/full-body.jpg',
  ],
};

// Muscles that don't have dedicated images. They get full-body as a fallback
// — better than wrong-muscle imagery, can be replaced when more photography
// arrives.
const FALLBACK_MUSCLE = 'full-body';

// Map every possible muscle string from the exercise data to a bucket above.
// Lowercased so callers can pass either 'Chest' or 'chest'.
const MUSCLE_ALIAS = {
  chest: 'chest',
  back: 'back',
  shoulders: 'shoulders',
  arms: 'arms',
  triceps: 'arms',
  biceps: 'arms',
  legs: 'legs',
  quads: 'legs',
  quadriceps: 'legs',
  hamstrings: 'hamstrings',
  glutes: 'glutes',
  core: 'core',
  abs: 'core',
  calves: 'calves',
  forearms: 'forearms',
  neck: 'neck',
  'full body': 'full-body',
  'full-body': 'full-body',
  fullbody: 'full-body',
  // Specialty/rehab groups → full-body until they get dedicated imagery
  palms: 'full-body',
  wrists: 'full-body',
  ankles: 'full-body',
  'hip flexors': 'full-body',
  'hip-flexors': 'full-body',
  adductors: 'full-body',
  'breath/diaphragm': 'full-body',
  'breath-diaphragm': 'full-body',
};

/**
 * Pick the image for one exercise.
 *
 * @param {string} muscle - section/muscle name from the exercise data
 * @param {number|string} exerciseId - stable id, used as the rotation seed
 * @returns {string} absolute URL path (always begins with /)
 */
export const getExerciseImage = (muscle, exerciseId = 0) => {
  const key = MUSCLE_ALIAS[String(muscle || '').toLowerCase()] || FALLBACK_MUSCLE;
  const bucket = MUSCLE_IMAGES[key] || MUSCLE_IMAGES[FALLBACK_MUSCLE];
  if (!bucket || bucket.length === 0) {
    // Should never happen because FALLBACK_MUSCLE always has entries, but
    // defend anyway so the UI doesn't render a broken <img src="">
    return '/library/categories/full-body.jpg';
  }
  if (bucket.length === 1) return bucket[0];
  // Deterministic by id so the same exercise always shows the same image.
  const idx = Math.abs(Number(exerciseId) || 0) % bucket.length;
  return bucket[idx];
};

export const MUSCLE_BUCKETS = MUSCLE_IMAGES; // exported for debug / admin tooling
